import type { ComponentType } from 'react'

/** Cai's products, one sidebar tab each. */
export enum ProductTab {
  Insurance = 'insurance',
  Compliance = 'compliance',
  Finance = 'finance',
}

/** The strip's order, the same in every app so a product never moves between them. */
export const PRODUCT_TABS: ProductTab[] = [ProductTab.Insurance, ProductTab.Compliance, ProductTab.Finance]

export const PRODUCT_TAB_LABEL: Record<ProductTab, string> = {
  [ProductTab.Insurance]: 'Insurance',
  [ProductTab.Compliance]: 'Compliance',
  [ProductTab.Finance]: 'Finance',
}

/** Cai's own tab, which leads the products in the top bar. */
export const CAI_TAB_LABEL = 'Cai'

/** Insurance is sold by the agency; everything else is the parent company. */
export const PRODUCT_TAB_ENTITY: Record<ProductTab, string> = {
  [ProductTab.Insurance]: 'Cai Insurance Services',
  [ProductTab.Compliance]: 'Cai Labs, Inc.',
  [ProductTab.Finance]: 'Cai Labs, Inc.',
}

/** Which list in the sidebar payload holds each tab's rows. */
export const PRODUCT_TAB_ROWS = {
  [ProductTab.Insurance]: 'insurance',
  [ProductTab.Compliance]: 'vault',
  [ProductTab.Finance]: 'finance',
} as const satisfies Record<ProductTab, string>

/** One row as a sidebar service sends it. */
export interface SidebarRow {
  title: string
  url: string
  /** A lucide icon name, resolved by sidebarIcon. */
  icon: string
  /** The heading the row sits under. */
  group: string | null
  /** Highlighted on this URL alone, not on the pages under it. */
  exact: boolean
  /** Drawn instead of icon on the collapsed rail. */
  collapsedIcon?: string | null
}

export type SidebarIcon = ComponentType<{ className?: string; color?: string; 'aria-hidden'?: 'true' }>

/** A row ready to draw. */
export interface SidebarLink {
  title: string
  /** Absolute for another app's page, otherwise a route in this one. */
  url: string
  icon: SidebarIcon
  collapsedIcon?: SidebarIcon
  exact?: boolean
}

export interface ProductPitch {
  label: string
  tag?: string
  pitch: string
  cta: string
  /** Absolute for another app's page, otherwise a route in this one. No CTA without one. */
  href?: string
  /** Who the product is open to, when that is not obvious. */
  note?: string
  /** Drawn to recede: the product cannot be bought yet, so it must not compete with one that can. */
  muted?: boolean
}

export function insurancePitch(href: string | undefined): ProductPitch {
  return {
    label: 'Insurance',
    pitch: 'Compare prices from carriers and get covered without a phone call.',
    cta: 'Get your quotes',
    href,
  }
}

/** The vault is sold to fleets Cai already insures; for any other fleet, pass no href. */
export function vaultPitch(href: string | undefined): ProductPitch {
  return {
    label: 'Compliance vault',
    tag: 'New',
    pitch: 'Keep every compliance document current, without chasing paperwork.',
    cta: 'Set up my vault',
    href,
    note: 'Only available to Cai insurance customers',
  }
}

export function bookkeepingPitch(href: string | undefined): ProductPitch {
  return {
    label: 'Bookkeeping',
    pitch: 'Connect your accounts and let Cai keep the books current.',
    cta: 'Set up my books',
    href,
  }
}

export function cfoPitch(href: string | undefined): ProductPitch {
  return {
    label: 'AI CFO',
    tag: 'Coming soon',
    pitch: 'See what every mile costs you and plan your next move.',
    cta: 'Get on the list',
    href,
    muted: true,
  }
}
